
import React, { useState, useEffect } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { Loader2, Users, Clock, CheckCircle2, TrendingUp, Filter, Sparkles } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { VacancyStage } from '../types';
import { generateVacancyAnalyticsOptimization } from '../services/geminiService';

interface VacancyAnalyticsProps {
    vacancyId: string;
    onClose: () => void;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const VacancyAnalytics: React.FC<VacancyAnalyticsProps> = ({ vacancyId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [stages, setStages] = useState<VacancyStage[]>([]);
    const [applications, setApplications] = useState<any[]>([]);

    // Metrics
    const [funnelData, setFunnelData] = useState<any[]>([]);
    const [timeToHire, setTimeToHire] = useState<number>(0);
    const [totalApplicants, setTotalApplicants] = useState<number>(0);
    const [conversionRate, setConversionRate] = useState<number>(0);
    const [vacancy, setVacancy] = useState<any>(null);
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [optimizationData, setOptimizationData] = useState<any>(null);

    useEffect(() => {
        fetchData();
    }, [vacancyId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch Stages
            const { data: stageData } = await supabase
                .from('vacancy_stages')
                .select('*')
                .eq('vacancy_id', vacancyId)
                .order('sort_order', { ascending: true });

            const currentStages = stageData || [];
            setStages(currentStages as any);

            // 2. Fetch Applications
            const { data: apps } = await supabase
                .from('applications')
                .select('*, candidates(name)')
                .eq('vacancy_id', vacancyId);

            const allApps = apps || [];
            setApplications(allApps);

            // 3. Fetch Vacancy details
            const { data: vacData } = await supabase
                .from('vacancies')
                .select('*')
                .eq('id', vacancyId)
                .single();
            if (vacData) setVacancy(vacData);

            processMetrics(currentStages as any, allApps);

        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const processMetrics = (currentStages: VacancyStage[], apps: any[]) => {
        // 1. Funnel Data
        // Count apps in each stage
        // Note: This is a "Snapshot" funnel (current state), not historical flow.
        // For true funnel, we'd need history. For now, snapshot.
        const funnel = currentStages.map(stage => {
            const count = apps.filter(a => {
                if (a.status === stage.id || a.status === stage.name) return true;
                // Legacy mapping
                if ((!a.status || a.status === 'New') && stage.type === 'Screening' && stage.sortOrder === 0) return true;
                return false;
            }).length;
            return { name: stage.name, count };
        });
        setFunnelData(funnel);

        // 2. Total Applicants
        setTotalApplicants(apps.length);

        // 3. Time to Hire (Avg days for 'Hired' candidates)
        // We assume 'Hired' stage type or status.
        const hiredApps = apps.filter(a => {
            const stage = currentStages.find(s => s.id === a.status || s.name === a.status);
            return stage?.type === 'Hired';
        });

        if (hiredApps.length > 0) {
            const totalDuration = hiredApps.reduce((acc, app) => {
                const start = new Date(app.created_at).getTime();
                const end = new Date(app.updated_at).getTime(); // updated_at is approx hire time if strictly moved
                return acc + (end - start);
            }, 0);
            setTimeToHire(Math.round(totalDuration / hiredApps.length / (1000 * 60 * 60 * 24))); // Days
            setConversionRate(Math.round((hiredApps.length / apps.length) * 100));
        } else {
            setTimeToHire(0);
            setConversionRate(0);
        }
    };

    const handleOptimize = async () => {
        if (!vacancy) return;
        setIsOptimizing(true);
        try {
            const metrics = {
                totalApplicants,
                timeToHire,
                conversionRate,
                activePipeline: applications.length - (funnelData[funnelData.length - 1]?.count || 0),
                funnelData
            };
            const result = await generateVacancyAnalyticsOptimization(vacancy, metrics);
            setOptimizationData(result);
        } catch (e) {
            console.error("AI Optimization failed", e);
            alert("Er is iets misgegaan bij het genereren van de analyse. Probeer het opnieuw.");
        } finally {
            setIsOptimizing(false);
        }
    };

    if (loading) return <div className="flex h-96 items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={32} /></div>;

    return (
        <div className="bg-slate-50 min-h-full p-8 animate-fadeIn">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header */}
                <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div>
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight">Analytics Dashboard</h2>
                        <p className="text-slate-500 font-medium">Prestaties van deze vacature</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={handleOptimize} 
                            disabled={isOptimizing}
                            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-6 py-3 rounded-xl font-bold transition-colors flex items-center gap-2 border border-indigo-100 disabled:opacity-50"
                        >
                            {isOptimizing ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                            Optimaliseer met Froukje AI
                        </button>
                        <button onClick={onClose} className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold transition-colors">
                            Terug naar Board
                        </button>
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <KpiCard icon={<Users className="text-red-0" />} label="Totaal Kandidaten" value={totalApplicants} change={12} />
                    <KpiCard icon={<CheckCircle2 className="text-emerald-500" />} label="Aangenomen" value={`${conversionRate}%`} sub="Conversie Rate" />
                    <KpiCard icon={<Clock className="text-amber-500" />} label="Avg. Time to Hire" value={`${timeToHire} Dagen`} sub="Sinds sollicitatie" />
                    <KpiCard icon={<TrendingUp className="text-purple-500" />} label="Actieve Pijplijn" value={applications.length - (funnelData[funnelData.length - 1]?.count || 0)} />
                </div>

                {/* AI Optimization Insight */}
                {optimizationData && (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-3xl shadow-sm border border-indigo-100 animate-fadeIn">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-indigo-100 text-indigo-600">
                                <Sparkles size={24} />
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-indigo-900">Froukje AI Analyse</h3>
                                <p className="text-indigo-700 text-sm font-medium">{optimizationData.commentary}</p>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {optimizationData.suggestions?.map((sug: any, idx: number) => (
                                <div key={idx} className="bg-white p-5 rounded-2xl border border-indigo-50 shadow-sm relative overflow-hidden group">
                                    <div className={`absolute top-0 left-0 w-1 h-full ${
                                        sug.impact === 'High' ? 'bg-red-500' : 
                                        sug.impact === 'Medium' ? 'bg-amber-400' : 'bg-emerald-400'
                                    }`}></div>
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-slate-900 text-sm leading-tight pl-2">{sug.title}</h4>
                                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                            sug.impact === 'High' ? 'bg-red-50 text-red-600' : 
                                            sug.impact === 'Medium' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                                        }`}>
                                            {sug.impact} Impact
                                        </span>
                                    </div>
                                    <p className="text-slate-500 text-xs pl-2">{sug.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Funnel Chart */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2"><Filter size={20} /> Recruitment Funnel</h3>
                        <div className="h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E2E8F0" />
                                    <XAxis type="number" hide />
                                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12, fontWeight: 600, fill: '#64748B' }} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        cursor={{ fill: '#F1F5F9' }}
                                    />
                                    <Bar dataKey="count" fill="#e51b23" radius={[0, 4, 4, 0]} barSize={32}>
                                        {funnelData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Distribution Pie */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                        <h3 className="text-lg font-bold text-slate-800 mb-6">Status Verdeling</h3>
                        <div className="h-80 w-full flex items-center justify-center">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={funnelData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        paddingAngle={5}
                                        dataKey="count"
                                    >
                                        {funnelData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" height={36} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

const KpiCard = ({ icon, label, value, sub, change }: any) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
        <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-slate-50 rounded-xl">
                {icon}
            </div>
            {change && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">+{change}%</span>}
        </div>
        <div>
            <h4 className="text-3xl font-black text-slate-900 tracking-tight">{value}</h4>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{label}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
    </div>
);

export default VacancyAnalytics;
